// Personal, unrefreshed snapshots. Retain the old array format and original
// bytes on read: neither a migration nor an automatic duplicate cleanup.
export const SAVED_LISTINGS_KEY = 'saved_car_listings'
const MAX_CHARACTERS = 4_000_000
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const scalar = value => value === null || ['string', 'number', 'boolean'].includes(typeof value)

export function listingBookmarkKey(car) {
  const country = car?.country_code, value = car?.listing_id
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return null
  const id = String(value ?? '')
  if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country) || !/^[1-9]\d{0,17}$/.test(id)) return null
  // IDs are country-scoped. Changed prices, releases and random React card IDs
  // must not make a second search for the same advert another bookmark.
  return `listing:${country}:${id}`
}
export function savedListingKey(car) {
  return listingBookmarkKey(car) || (typeof car?.id === 'string' && car.id ? `snapshot:${car.id}` : null)
}
function validSnapshot(car) {
  return object(car) && typeof car.id === 'string' && car.id.length > 0 && car.id.length <= 4096 &&
    typeof car.make === 'string' && typeof car.model === 'string' &&
    Object.entries(car).every(([key, value]) => scalar(value) ||
      (key === 'recommendation_evidence' && object(value) && Object.values(value).every(scalar)) ||
      (key === 'economics_issues' && Array.isArray(value) && value.every(issue => typeof issue === 'string')))
}
export function readSavedListings(storage) {
  let raw
  try { if (!storage) throw Error(); raw = storage.getItem(SAVED_LISTINGS_KEY) }
  catch { return { items: [], raw: null, error: 'unavailable' } }
  try {
    if (raw === null) return { items: [], raw, error: null }
    if (raw.length > MAX_CHARACTERS) throw Error()
    const items = JSON.parse(raw)
    if (!Array.isArray(items) || !items.every(validSnapshot)) throw Error()
    return { items, raw, error: null }
  } catch { return { items: [], raw, error: 'invalid' } }
}
// Acknowledge only accepted writes. Observed stale-tab changes are refused;
// localStorage has no atomic cross-tab compare-and-swap, so this is not one.
export function toggleSavedListing(storage, expected, car) {
  const previous = readSavedListings(storage)
  if (previous.error) return { ...expected, error: previous.error, action: null }
  if (previous.raw !== expected.raw) return { ...previous, error: 'conflict', action: null }
  const key = listingBookmarkKey(car)
  // From Saved, remove the exact historical snapshot, not all duplicates.
  let index = previous.items.findIndex(item => item.id === car?.id && listingBookmarkKey(item) === key)
  if (index < 0 && key) index = previous.items.findIndex(item => listingBookmarkKey(item) === key)
  if (index < 0 && !key) return { ...previous, error: 'identity', action: null }
  const items = [...previous.items]
  if (index >= 0) items.splice(index, 1)
  else items.push(car)
  try {
    const raw = JSON.stringify(items), serialized = JSON.parse(raw)
    if (!serialized.every(validSnapshot)) return { ...previous, error: 'invalid', action: null }
    if (raw.length > MAX_CHARACTERS) return { ...previous, error: 'size', action: null }
    storage.setItem(SAVED_LISTINGS_KEY, raw)
    return { items: serialized, raw, error: null, action: index >= 0 ? 'removed' : 'saved' }
  } catch { return { ...previous, error: 'unavailable', action: null } }
}
export const savedListingMessages = Object.freeze({
  unavailable: 'Browser storage is unavailable or full. This save or removal was not confirmed. Your existing stored snapshots have not been replaced.',
  invalid: 'Saved listing data could not be read safely. It has not been overwritten or removed. Your other saved plans are separate.',
  conflict: 'Saved listings changed in another tab. The latest list is now shown; review it before trying again.',
  identity: 'This advert has no reliable country and listing ID, so it cannot be saved as a new bookmark.',
  size: 'Saved listing storage is too large. Remove an unwanted snapshot before saving another.',
  saved: 'Listing snapshot saved on this browser. Its prices and availability are not refreshed.',
  removed: 'One listing snapshot removed from this browser. Other snapshots and saved plans are unchanged.',
})
